import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const schemaData = article
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heilao-buda.com/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://heilao-buda.com/category/${article.category}`,
              },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
          {
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Organization', name: '黑料不打' },
            publisher: { '@type': 'Organization', name: '黑料不打' },
            url: `https://heilao-buda.com/article/${article.id}`,
            articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 500),
          },
        ],
      }
    : {};

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="mt-4 text-foreground-500">文章不存在或已被删除</p>
            <Link to="/" className="mt-4 inline-flex items-center text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50 border-b border-background-200/60">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="inline-flex items-center px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {article.categoryName}
                </span>
                {article.verified && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full whitespace-nowrap">
                    <i className="ri-shield-check-line text-[10px]"></i> 内容已核实
                  </span>
                )}
                <span className="text-xs text-foreground-400">{article.date} 发布</span>
                <span className="text-xs text-foreground-400">{article.views.toLocaleString()} 阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed border-l-3 border-primary-400 pl-4">
                <strong>摘要：</strong>{article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-0.5 bg-background-100 text-foreground-500 text-xs rounded whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">被评价品牌：</span>
                  <span className="text-sm font-semibold text-foreground-900">{article.brandName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">口碑评分：</span>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-primary-600">
                    {'★'.repeat(Math.floor(article.rating))}{'☆'.repeat(5 - Math.floor(article.rating))}
                    <span className="text-foreground-500 font-normal ml-1">{article.rating}/5</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8 p-5 bg-accent-50 border border-accent-200/60 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-information-line"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-accent-800 mb-1">黑料不打声明</p>
                  <p className="text-xs text-accent-700 leading-relaxed">
                    本文内容已经过黑料不打编辑团队的核实与交叉验证。我们致力于呈现客观、真实的信息，
                    但不构成对任何品牌或个人的法律结论。如有补充或更正，请联系我们。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                更多{article.categoryName}相关报道
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="p-4 bg-background-50 rounded-lg cursor-pointer group hover:bg-primary-50/50 transition-colors duration-200"
                  >
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{ra.excerpt}</p>
                    <span className="text-xs text-foreground-400 mt-3 block">{ra.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}